import React, { ChangeEvent } from "react";
import ReactModal from "react-modal";
import { DateTime, Duration } from "luxon";

import { V2 } from "../../Api";
import { surgeryStatusToText } from "../../TextTables";
import { formatDateLocalNumeric, formatDateTimeLocalNumeric, formatName, formatTimeLocal } from "../../Utilities";
import { RouteComponentPropsHistory } from "../../types";

import withTitle from "../withTitle";
import withAuth from "../withAuth";
import { IAuthProps } from "../AuthContext";

type CheckInDialogState = "selector" | "checkin" | "checkout" | "move";

interface ISurgeryPatientListViewState {
    isLoading: boolean | string;
    isCheckInDialogOpen: boolean;
    isCheckInDialogSubmitting: boolean | string;
    checkInDialogState: CheckInDialogState;

    locations: any[];
    appointments: any[];
    locationRooms: any[];

    selectedDate: string;
    selectedLocation: string;
    refreshTimestamp: string;

    checkInId: string;
    clickedSurgery: any;
    checkInTime: string;
    checkInLocation: string;
    checkOutTime: string;
    newRoomNumber: string;
}

interface ISurgeryPatientListViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {

}

class SurgeryPatientListView extends React.Component<ISurgeryPatientListViewProps, ISurgeryPatientListViewState> {
    private _loadTimeout: number | null = null;

    constructor(props: Readonly<ISurgeryPatientListViewProps>) {
        super(props);

        const state: ISurgeryPatientListViewState = {
            isLoading: true,
            isCheckInDialogOpen: false,
            isCheckInDialogSubmitting: false,
            checkInDialogState: "selector",

            locations: [],
            appointments: [],
            locationRooms: [],
            refreshTimestamp: "",

            selectedDate: DateTime.local().toISODate(),
            selectedLocation: "",

            checkInId: "",
            clickedSurgery: {},
            checkInTime: "",
            checkInLocation: "",
            checkOutTime: "",
            newRoomNumber: "",
        };

        this.state = state;

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleDialogInputChange = this.handleDialogInputChange.bind(this);
        this.handleRowClick = this.handleRowClick.bind(this);
        this.closeCheckInDialog = this.closeCheckInDialog.bind(this);
        this.checkInPatient = this.checkInPatient.bind(this);
        this.checkOutPatient = this.checkOutPatient.bind(this);
        this.movePatient = this.movePatient.bind(this);
    }

    public componentDidMount() {
        this.loadDataOnce();
    }

    public componentWillUnmount() {
        if (this._loadTimeout) {
            window.clearTimeout(this._loadTimeout);
        }
    }

    public componentDidUpdate(prevProps: Readonly<ISurgeryPatientListViewProps>, prevState: Readonly<ISurgeryPatientListViewState>) {
        if (this.state.selectedLocation !== prevState.selectedLocation) {
            this.loadLocationInfo();
        }
    }

    public render() {
        return (
            <div className="viewer-root">
                <ReactModal isOpen={this.state.isCheckInDialogOpen} onRequestClose={this.closeCheckInDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-content-500" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open"
                    contentLabel="Check in/out patient">

                    <div className="input-form">
                        <div className="info-block">
                            <header>
                                <h3>Check in/out patient</h3>
                            </header>
                            {this.state.checkInDialogState !== "move" ?
                                <>
                                    <section>
                                        <span>You are checking in/out <b>{formatName(this.state.clickedSurgery.patient?.name)}</b> #{this.state.clickedSurgery.patient?.chartNumber}</span>
                                    </section>
                                    &nbsp;
                                </>
                                : null}
                            {this.renderDialogBody()}
                            {typeof this.state.isCheckInDialogSubmitting === "string"
                                ? <div className="error-text">{this.state.isCheckInDialogSubmitting}</div>
                                : null}
                        </div>
                    </div>
                </ReactModal>
                <div className="filter-control-box">
                    <button type="button" className="button small text-with-icon" onClick={() => this.loadData(true)}><i className="icon-refresh" /><span>Refresh</span></button>
                    {this.state.refreshTimestamp ? <span style={{ alignSelf: "center" }}>As of {formatDateTimeLocalNumeric(this.state.refreshTimestamp)}</span> : null}

                    <input type="date" className="push-right" name="selectedDate" value={this.state.selectedDate} onChange={this.handleInputChange} />
                    <select name="selectedLocation" value={this.state.selectedLocation} onChange={this.handleInputChange}>
                        <option value="">All Locations</option>
                        {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.shortName || a.name}</option>)}
                    </select>
                </div>
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">Date</th>
                            <th className="col-smallest">Time</th>
                            <th className="col-smallest">In Time</th>
                            <th className="col-smallest">Out Time</th>
                            <th className="col-smallest">Room</th>
                            <th className="col-smallest">Chart #</th>
                            <th>Name</th>
                            <th>Operation</th>
                            <th>Surgeon</th>
                            <th className="col-smallest">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoading === true
                            ? <tr><td colSpan={10}><div className="spinner spinner-center lds-dual-ring" /></td></tr>
                            : typeof this.state.isLoading === "string"
                                ? <tr><td colSpan={10}><div className="error-text">{this.state.isLoading}</div></td></tr>
                                : this.state.appointments.map(a => <tr key={a.id} className={`clickable ${this.surgeryToClass(a)}`} onClick={() => this.handleRowClick(a.id)}>
                                    <td>{formatDateLocalNumeric(a.date)}</td>
                                    <td>{formatTimeLocal(a.time)}</td>
                                    <td>{formatTimeLocal(a.checkInInfo?.inTime)}</td>
                                    <td>{formatTimeLocal(a.checkInInfo?.outTime)}</td>
                                    <td>{a.checkInInfo?.room?.name}</td>
                                    <td>{a.patient.chartNumber}</td>
                                    <td>{formatName(a.patient.name)}</td>
                                    <td>{a.primarySurgery.shortName || a.primarySurgery.name}</td>
                                    <td>{formatName(a.surgeon.name)}</td>
                                    <td>{surgeryStatusToText(a.status)}</td>
                                </tr>)}
                    </tbody>
                </table>
            </div >
        );
    }

    private renderDialogBody() {
        switch (this.state.checkInDialogState) {
            case "selector":
                return this.renderSelectorDialogBody();
            case "checkin":
                return this.renderCheckInDialogBody();
            case "checkout":
                return this.renderCheckOutDialogBody();
            case "move":
                return this.renderMoveDialogBody();
            default:
                return null;
        }
    }

    private renderSelectorDialogBody() {
        return <>
            <div className="action-panel-dialog">
                <span className="spring" />
                <button type="button" className="button" onClick={() => this.goSurgery(this.state.checkInId)}>View Surgery</button>
            </div>
            &nbsp;
            <div className="action-panel-dialog">
                <span className="spring" />
                <button type="button" className="button" onClick={this.closeCheckInDialog}>Cancel</button>
                <button type="button" className="button green text-with-icon" onClick={() => this.setDialogState("checkin")}><i className="icon-sign-in" /><span>Check in</span></button>
                <button type="button" className="button text-with-icon" onClick={() => this.setDialogState("move")} disabled={!this.state.clickedSurgery.checkInInfo?.inTime || this.state.clickedSurgery.checkInInfo?.outTime}><i className="icon-arrow-right" /><span>Move</span></button>
                <button type="button" className="button green text-with-icon" onClick={() => this.setDialogState("checkout")}><i className="icon-sign-out" /><span>Check out</span></button>
            </div>
        </>;
    }

    private renderMoveDialogBody() {
        return <>
            <section>
                <span>Patient <b>{formatName(this.state.clickedSurgery.patient?.name)}</b> #{this.state.clickedSurgery.patient?.chartNumber} is currently in room {this.state.clickedSurgery.checkInInfo?.roomNumber}</span>
            </section>
            &nbsp;
            <section>
                <div className="input-form-group">
                    <label htmlFor="newRoomNumber">Move to Room #</label>
                    <select id="newRoomNumber" name="newRoomNumber" value={this.state.newRoomNumber} onChange={this.handleDialogInputChange}>
                        <option></option>
                        {this.state.locationRooms.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>
            </section>
            &nbsp;
            <div className="action-panel-dialog">
                <button type="button" className="button text-with-icon" onClick={() => this.setDialogState("selector")}><i className="icon-chevron-left" /><span>Back</span></button>
                <span className="spring" />
                <button type="button" className="button green text-with-icon" onClick={this.movePatient} disabled={this.state.isCheckInDialogSubmitting === true}><i className="icon-arrow-right" /><span>Move</span></button>
            </div>
        </>;
    }

    private renderCheckInDialogBody() {
        return <>
            <section>
                <div className="input-form-group">
                    <label htmlFor="checkInTime">Check-in time</label>
                    <input type="time" id="checkInTime" name="checkInTime" value={this.state.checkInTime} onChange={this.handleDialogInputChange} />
                </div>
                <div className="input-form-group">
                    <label htmlFor="checkInLocation">Room #</label>
                    <select id="checkInLocation" name="checkInLocation" value={this.state.checkInLocation} onChange={this.handleDialogInputChange}>
                        <option></option>
                        {this.state.locationRooms.filter(a => a.type === "pacu").map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>
            </section>
            &nbsp;
            <div className="action-panel-dialog">
                <button type="button" className="button text-with-icon" onClick={() => this.setDialogState("selector")}><i className="icon-chevron-left" /><span>Back</span></button>
                <span className="spring" />
                <button type="button" className="button green text-with-icon" onClick={this.checkInPatient} disabled={this.state.isCheckInDialogSubmitting === true}><i className="icon-sign-in" /><span>Check in</span></button>
            </div>
        </>;
    }

    private renderCheckOutDialogBody() {
        return <>
            <section>
                <div className="input-form-group">
                    <label htmlFor="checkOutTime">Check-out time</label>
                    <input type="time" id="checkOutTime" name="checkOutTime" value={this.state.checkOutTime} onChange={this.handleDialogInputChange} />
                </div>
                <div className="input-form-group">
                </div>
            </section>
            &nbsp;
            <div className="action-panel-dialog">
                <button type="button" className="button text-with-icon" onClick={() => this.setDialogState("selector")}><i className="icon-chevron-left" /><span>Back</span></button>
                <span className="spring" />
                <button type="button" className="button green text-with-icon" onClick={this.checkOutPatient} disabled={this.state.isCheckInDialogSubmitting === true}><i className="icon-sign-out" /><span>Check out</span></button>
            </div>
        </>;
    }

    private handleInputChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name as keyof ISurgeryPatientListViewState;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        } as any, () => this.loadDataDelayed());
    }

    private handleDialogInputChange(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name as keyof ISurgeryPatientListViewState;
        const value = e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private async loadLocationInfo() {
        try {
            const result = await V2.get<any>(`locations/${this.state.selectedLocation}/rooms`);

            this.setState({
                locationRooms: result.rooms
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load location information"
            });
        }
    }

    private loadDataDelayed(suppressLoading = false) {
        if (this._loadTimeout) {
            window.clearTimeout(this._loadTimeout);
        }

        this._loadTimeout = window.setTimeout(() => this.loadData(suppressLoading), 300);
    }

    private async loadDataOnce() {
        try {
            const defaultLocation = this.props.authContext.user.employee.locationId;

            const result = await V2.get<any>(`locations/${defaultLocation}/related?relation=surgery`);

            this.setState({
                selectedLocation: result.relatedLocations[0].id
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load location list"
            });
        }
    }

    private async loadData(suppressLoading = false) {
        try {
            if (!suppressLoading) {
                this.setState({
                    isLoading: true
                });
            }

            const params = [
                `date=${this.state.selectedDate}`,
                `location=${this.state.selectedLocation}`
            ];

            const [results, locations] = await Promise.all([
                V2.get<any>(`surgery/patient-list?${params.join("&")}`),
                V2.get<any[]>("locations?type=surgery"),
            ]);

            this.setState({
                isLoading: false,
                appointments: results.list,
                locations,
                refreshTimestamp: DateTime.local().toISO()
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load patient list"
            });
        }
    }

    private surgeryToClass(surgery: any) {
        const classes = [];

        switch (surgery.status) {
            case 2: // Canceled
            case 3: // No-show
            case 5: // Not needed
            case 6: // Failed
                classes.push("row-gray", "row-gray-text", "row-strike");
                break;
            //case 4: // Complete
            //    classes.push("row-green");
            //    break;
        }

        if (surgery.checkInInfo?.inTime && !surgery.checkInInfo?.outTime) {
            // Currently checked in
            classes.push("row-bold", "row-green");
        }

        if (surgery.checkInInfo?.outTime) {
            // Checked out
            classes.push("row-gray", "row-gray-text")
        }

        return classes.length > 0
            ? classes.join(" ")
            : undefined;
    }

    private handleRowClick(id: string) {
        const clickedSurgery = this.state.appointments.find(a => a.id === id);

        this.setState({
            isCheckInDialogOpen: true,
            checkInId: id,
            clickedSurgery
        });
    }

    private closeCheckInDialog() {
        this.setState({
            isCheckInDialogOpen: false,
            checkInDialogState: "selector",
            checkInLocation: ""
        });
    }

    private setDialogState(state: CheckInDialogState) {
        const st: Partial<ISurgeryPatientListViewState> = {
            checkInDialogState: state
        };

        switch (state) {
            case "checkin": {
                st.checkInTime = st.checkInTime || DateTime.local().toISOTime().substr(0, 8);
                break;
            }
            case "checkout": {
                st.checkOutTime = st.checkOutTime || DateTime.local().toISOTime().substr(0, 8);
                break;
            }
        }

        this.setState(st as any);
    }

    private async checkInPatient() {
        if (!this.state.checkInTime)
            return;

        try {
            this.setState({
                isCheckInDialogSubmitting: true
            });

            const data = {
                inTime: DateTime.local().startOf("day").plus(Duration.fromISOTime(this.state.checkInTime)).toISO(),
                outTime: null,
                roomId: this.state.checkInLocation
            };

            await V2.patch(`surgery/${this.state.checkInId}/check-in`, data);

            this.setState({
                isCheckInDialogSubmitting: false,
                checkInDialogState: "selector",
                isCheckInDialogOpen: false
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isCheckInDialogSubmitting: e.message || "Could not check in patient"
            });
        }
    }

    private async checkOutPatient() {
        if (!this.state.checkOutTime)
            return;

        try {
            this.setState({
                isCheckInDialogSubmitting: true
            });

            const data = {
                outTime: DateTime.local().startOf("day").plus(Duration.fromISOTime(this.state.checkOutTime)).toISO(),
                roomId: null
            };

            await V2.patch(`surgery/${this.state.checkInId}/check-in`, data);

            this.setState({
                isCheckInDialogSubmitting: false,
                checkInDialogState: "selector",
                isCheckInDialogOpen: false
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isCheckInDialogSubmitting: e.message || "Could not check out patient"
            });
        }
    }

    private async movePatient() {
        if (!this.state.newRoomNumber)
            return;

        try {
            this.setState({
                isCheckInDialogSubmitting: true
            });

            const data = {
                roomId: this.state.newRoomNumber
            };

            await V2.patch(`surgery/${this.state.checkInId}/check-in`, data);

            this.setState({
                isCheckInDialogSubmitting: false,
                checkInDialogState: "selector",
                isCheckInDialogOpen: false
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isCheckInDialogSubmitting: e.message || "Could not move patient"
            });
        }
    }

    private goSurgery(id: string) {
        this.props.history.push(`/surgery/${id}`);
    }
}

export default withAuth(withTitle("Patient List", SurgeryPatientListView));
